import Events from './backbone.events';
import device from './device';
import merge from './merge';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const each = (jwplayer as any)._.each as <T = any>(
    list: T[] | Record<string, T>,
    iteratee: (
        value: T,
        keyOrIndex: any, // eslint-disable-line @typescript-eslint/no-explicit-any
        list: T[] | Record<string, T>,
    ) => void,
    context?: unknown,
) => void;

/**
 * Create an HTMLElement from an HTML string.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const createNode = (jwplayer as any).utils.createElement as (
    html: string,
) => HTMLElement;

type TemplateFn = (data: object) => string;

/**
 * Template system borrowed from http://ejohn.org/blog/javascript-micro-templating/
 */
const template = (function () {
    const cache: Record<string, TemplateFn> = {};

    return function tmpl(str: string, data?: object): string {
        // Figure out if we're getting a template, or if we need to
        // load the template - and be sure to cache the result.
        let fn: TemplateFn;

        if (!/\W/.test(str)) {
            if (!cache[str]) {
                cache[str] = tmpl(
                    (document.getElementById(str) as HTMLElement).innerHTML,
                ) as unknown as TemplateFn;
            }
            fn = cache[str] as TemplateFn;
        } else {
            // Generate a reusable function that will serve as a template
            // generator (and which will be cached).
            fn = new Function(
                'obj',
                `${
                    'var p=[],print=function(){p.push.apply(p,arguments);};' +
                    // Introduce the data as local variables using with(){}
                    "with(obj){p.push('"
                }${
                    // Convert the template into pure JavaScript
                    str
                        .replace(/[\r\t\n]/g, ' ')
                        .split('<%')
                        .join('\t')
                        .replace(/((^|%>)[^\t]*)'/g, '$1\r')
                        .replace(/\t=(.*?)%>/g, "',$1,'")
                        .split('\t')
                        .join("');")
                        .split('%>')
                        .join("p.push('")
                        .split('\r')
                        .join("\\'")
                }');}return p.join('');`,
            ) as TemplateFn;
        }

        // Provide some basic currying to the user
        return data ? fn(data) : fn({});
    };
})();

const utils = {
    Events: Events,
    extend: Object.assign,
    // merge with deep copy, similar to jQuery extend
    merge: merge,
    each,
    isNumber: (data: unknown): data is number =>
        typeof data === 'number' || data instanceof Number,
    isObject: (data: unknown) => {
        const type = typeof data;
        return data != null && (type === 'object' || type === 'function');
    },
    createNode,
    /**
     * @param {HTMLElement} element
     * @param {string | string[]} classList
     */
    addClass: (element: HTMLElement, classList: string | string[]) => {
        if (Array.isArray(classList)) {
            element.classList.add(...classList);
        } else {
            element.classList.add(classList);
        }
    },
    /**
     * @param {HTMLElement} element
     * @param {string | string[]} classList
     */
    removeClass: (element: HTMLElement, classList: string | string[]) => {
        if (Array.isArray(classList)) {
            element.classList.remove(...classList);
        } else {
            element.classList.remove(classList);
        }
    },

    template,

    device: device,

    time: {
        shareTimeToSeconds: function (time: string): number {
            const parts = time.split(/[msh]/).filter(Boolean);
            const length = parts.length;

            const seconds = parseInt(parts[length - 1] ?? '0', 10);
            const minutes = parts[length - 2];
            const hours = parts[length - 3];

            return (
                seconds +
                (minutes ? parseInt(minutes, 10) * 60 : 0) +
                (hours ? parseInt(hours, 10) * 3600 : 0)
            );
        },
    },

    number: {
        zeroPad: function (number: number | string): string {
            const n = parseInt(number as string, 10);
            return n < 10 ? `0${n}` : n.toString();
        },
    },

    string: {
        // timestamp [s]
        getTimeFormatted: function (timestamp: number): string {
            const hours = Math.floor(timestamp / 3600),
                minutes = Math.floor((timestamp % 3600) / 60),
                seconds = timestamp % 60;

            return `${
                (hours > 0 ? `${hours}:` : '') + utils.number.zeroPad(minutes)
            }:${utils.number.zeroPad(seconds)}`;
        },
    },

    /**
     * Check snap points support
     * @returns {string | false}
     */
    getScrollSnapPrefix: function (): string | false {
        if ('webkitScrollSnapType' in document.documentElement.style) {
            return '-webkit-';
        } else if ('msScrollSnapType' in document.documentElement.style) {
            return '-ms-';
        } else if ('scrollSnapType' in document.documentElement.style) {
            return '';
        }

        return false;
    },

    jsonToUrl: function (
        obj: Record<string, string | number | boolean>,
    ): string {
        return Object.keys(obj)
            .map(function (key) {
                return `${encodeURIComponent(key)}=${encodeURIComponent(
                    obj[key] as string,
                )}`;
            })
            .join('&');
    },
};

export default utils;
